import React, { useState, useEffect, useRef, useCallback } from 'react';

// ─── Configuration ──────────────────────────────────────────────────────────

const ROW_COUNT = 6;
const COL_COUNT = 4;
const TOTAL_CELLS = ROW_COUNT * COL_COUNT;

/** Header widths per column — varied. */
const HEADER_WIDTHS = ['60%', '50%', '45%', '40%'];

/** Pseudo-random cell content widths — shorter placeholders, varied per row. */
const CELL_WIDTHS: string[][] = [
  ['65%', '45%', '70%', '35%'],
  ['42%', '72%', '50%', '58%'],
  ['78%', '38%', '60%', '48%'],
  ['55%', '62%', '35%', '72%'],
  ['68%', '50%', '42%', '55%'],
  ['48%', '70%', '58%', '35%'],
];

// ─── Irregular movement logic ───────────────────────────────────────────────

/** Random int in [min, max]. */
function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/** Pick next cell index — irregular, AI-like pattern. */
function pickNextCell(current: number): number {
  const roll = Math.random();
  const row = Math.floor(current / COL_COUNT);
  const col = current % COL_COUNT;

  if (roll < 0.6) {
    // 60% → next column (wrap to next row if at end)
    return (current + 1) % TOTAL_CELLS;
  } else if (roll < 0.8) {
    // 20% → next row, same column
    const nextRow = (row + 1) % ROW_COUNT;
    return nextRow * COL_COUNT + col;
  } else {
    // 20% → random cell (avoid same cell)
    let next = randInt(0, TOTAL_CELLS - 1);
    while (next === current) {
      next = randInt(0, TOTAL_CELLS - 1);
    }
    return next;
  }
}

/** Random delay between moves: 80–260ms, occasional pause every 5–8 moves. */
function getNextDelay(moveCount: number, pauseAt: number): number {
  if (moveCount >= pauseAt) {
    return randInt(400, 700); // pause
  }
  return randInt(80, 260); // normal move
}

// ─── Component ──────────────────────────────────────────────────────────────

/**
 * Table skeleton with irregular AI-like scanning highlight.
 *
 * - Lighter header (#F0F0F0) with smaller header blocks
 * - 6 rows with 2px spacing between them
 * - Shorter cell bars (10px height)
 * - 4px border-radius on cells
 * - One highlight that jumps cell-to-cell (no sliding)
 */
interface TableSkeletonProps {
  /** Real chart title from plan preview — replaces shimmer placeholder. */
  title?: string;
}

const TableSkeleton: React.FC<TableSkeletonProps> = ({ title }) => {
  const [scanIndex, setScanIndex] = useState(0);
  const [prevIndex, setPrevIndex] = useState(-1);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [cellSize, setCellSize] = useState({ w: 0, h: 0 });
  const moveCountRef = useRef(0);
  const pauseAtRef = useRef(randInt(5, 8));

  // Measure cell dimensions
  const measureCells = useCallback(() => {
    if (!bodyRef.current) return;
    const firstCell = bodyRef.current.querySelector('.table-skeleton__cell');
    if (firstCell) {
      const rect = firstCell.getBoundingClientRect();
      setCellSize({ w: rect.width, h: rect.height });
    }
  }, []);

  useEffect(() => {
    measureCells();

    const observer = new ResizeObserver(() => measureCells());
    if (bodyRef.current) {
      observer.observe(bodyRef.current);
    }

    return () => observer.disconnect();
  }, [measureCells]);

  // Irregular scanning loop
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const step = () => {
      moveCountRef.current += 1;
      const delay = getNextDelay(moveCountRef.current, pauseAtRef.current);

      // Reset pause counter after a pause
      if (moveCountRef.current >= pauseAtRef.current) {
        moveCountRef.current = 0;
        pauseAtRef.current = randInt(5, 8);
      }

      setScanIndex((prev) => {
        const next = pickNextCell(prev);
        setPrevIndex(prev);
        return next;
      });

      timeoutId = setTimeout(step, delay);
    };

    timeoutId = setTimeout(step, randInt(80, 260));
    return () => clearTimeout(timeoutId);
  }, []);

  const getTransform = (idx: number) => {
    const row = Math.floor(idx / COL_COUNT);
    const col = idx % COL_COUNT;
    // Account for row spacing (2px margin-top + 1px border-bottom between rows)
    const rowOffset = row * 3;
    return `translate(${col * cellSize.w}px, ${row * cellSize.h + rowOffset}px)`;
  };

  return (
    <div className="skeleton-chart-container">
      <div className="table-skeleton">
        {title ? (
          <div className="chart-skeleton__real-title">{title}</div>
        ) : (
          <div className="skeleton-block table-skeleton__title" />
        )}

        {/* Header — static */}
        <div className="table-skeleton__header">
          {Array.from({ length: COL_COUNT }, (_, i) => (
            <div key={i} className="table-skeleton__header-cell">
              <div
                className="table-skeleton__header-block"
                style={{ width: HEADER_WIDTHS[i] }}
              />
            </div>
          ))}
        </div>

        {/* Body — static grid + jumping highlight */}
        <div className="table-skeleton__body" ref={bodyRef}>
          {Array.from({ length: ROW_COUNT }, (_, rowIdx) => (
            <div key={rowIdx} className="table-skeleton__row">
              {Array.from({ length: COL_COUNT }, (_, colIdx) => (
                <div key={colIdx} className="table-skeleton__cell">
                  <div
                    className="table-skeleton__cell-bar"
                    style={{
                      width: CELL_WIDTHS[rowIdx % CELL_WIDTHS.length][colIdx],
                    }}
                  />
                </div>
              ))}
            </div>
          ))}

          {cellSize.w > 0 && (
            <>
              {/* Trail — previous cell, fading */}
              {prevIndex >= 0 && (
                <div
                  key={`trail-${prevIndex}`}
                  className="table-skeleton__scan-trail"
                  style={{
                    width: cellSize.w,
                    height: cellSize.h,
                    transform: getTransform(prevIndex),
                  }}
                />
              )}
              {/* Active highlight — jumps instantly */}
              <div
                key={`scan-${scanIndex}`}
                className="table-skeleton__scan-cell"
                style={{
                  width: cellSize.w,
                  height: cellSize.h,
                  transform: getTransform(scanIndex),
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(TableSkeleton);